import React, { Fragment, useEffect, useRef, useState } from "react";
import DefaultProfilePicture from "../../common/defaultProfilePicture/DefaultProfilePicture";
import { useMatch, useNavigate } from "react-router-dom";
import ScannedLeadsNotes from "./ScannedLeadsNotes";
import { Listbox, Transition } from "@headlessui/react";
import useDebounce from "../../helper/hooks/useDebounce.jsx";
import {
  useFetchLeads,
  useFetchLeadsCount,
} from "../../tanstack queries/leadsQueries.js";
import { getAuthenticatedRequest } from "../../utils/API/api.ts";
import { showToast } from "../../redux/actions/toastActions.js";
import { useDispatch } from "react-redux";

const ScannedLeads = () => {
  const [attendeesData, setAttendeesData] = useState([]);
  const eventsId = useMatch("/attendee/:eventId/*");
  const [triggerNotes, setTriggerNotes] = useState(false);
  const [singleLeadsData, setSingleLeadsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loadMoreRef = useRef(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const options = [
    { label: "All Leads", value: "" },
    { label: "Hot Leads", value: "hot" },
    { label: "Warm Leads", value: "warm" },
    { label: "Cold Leads", value: "cold" },
  ];
  const [leadCount, setLeadCount] = useState({
    hot: 0,
    all: 0,
    warm: 0,
    cold: 0,
  });

  const [currentLead, setCurrentLead] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  // const [searchResults, setSearchResults] = useState([]);
  const debouncedSearchValue = useDebounce(searchTerm, 500);
  const {
    data: leadsCountData,
    error: leadsCountError,
    isLoading: isLoadingCounts,
  } = useFetchLeadsCount({
    eventId: eventsId.params.eventId,
  });

  const {
    data: leadsData,
    error: leadsDataError,
    isLoading: isLoadingLeads,
  } = useFetchLeads({
    eventId: eventsId?.params?.eventId,
    currentLead,
    page,
    searchValue: debouncedSearchValue,
  });

  useEffect(() => {
    if (leadsData) {
      if (debouncedSearchValue) {
        setAttendeesData(leadsData);
        setHasMore(false);
      } else {
        setAttendeesData((prevData) =>
          page === 1 ? leadsData : [...prevData, ...leadsData]
        );

        setHasMore(leadsData.length === 10);
      }
    }

    if (leadsDataError) {
      console.log(leadsDataError);
    }
  }, [leadsData, leadsDataError, page, debouncedSearchValue]);

  useEffect(() => {
    if (leadsCountData) {
      setLeadCount({ ...leadsCountData?.counts });
    } else if (page > 1) {
      setHasMore(false);
    }
    if (leadsCountError) {
      console.log(leadsCountError);
    }
  }, [leadsCountData, leadsCountError, eventsId.params.eventId, page]);

  useEffect(() => {
    setPage(1);
    setHasMore(true);
  }, [currentLead, searchTerm, eventsId.params.eventId]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (
          target.isIntersecting &&
          hasMore &&
          !isLoadingLeads &&
          !debouncedSearchValue
        ) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 1 }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [hasMore, debouncedSearchValue, isLoadingLeads]);

  const handleDownload = async () => {
    try {
      setLoading(true);
      const response = await getAuthenticatedRequest(
        `/attendee/getLeadDetails?eventId=${eventsId?.params?.eventId}&limit=0`
      );
      const data = response?.data?.leads;
      const fields = [
        "firstName",
        "lastName",
        "email",
        "qualifiedAs",
        "meetingTime",
        "countryCode",
        "mobile",
        "organization",
        "jobTitle",
        "assignedTo",
        "leadRating",
        "additionalNotes",
      ];
      const headerRow = fields.join(",");
      const dataRows = data.map((row) => {
        let defaultFields = [
          "firstName",
          "lastName",
          "email",
          "countryCode",
          "mobile",
          "organization",
          "jobTitle",
        ];
        return fields
          .map((field) => {
            if (defaultFields.includes(field)) {
              return row.attendee[field]
                ? String(row.attendee[field])?.replace(/,/g, "-")
                : "N/A";
            } else {
              return field === "meetingTime"
                ? `${formatDate(row[field])?.replace(/,/g, "-")} IST`
                : row[field]
                ? String(row[field]).replace(/,/g, "-")
                : "N/A";
            }
          })
          .join(",");
      });
      const csvContent =
        "data:text/csv;charset=utf-8," + [headerRow, ...dataRows].join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `Leads.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => {
        setLoading(false);
        dispatch(
          showToast({
            message: "Leads downloaded successfully!",
            category: "success",
          })
        );
      }, 500);
    } catch (err) {
      console.log(err);
      dispatch(
        showToast({
          message: "Some error occurred",
          category: "danger",
        })
      );
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (date) {
      const utcDate = new Date(date);

      const options = {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour12: true,
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        minute: "2-digit",
      };
      const formatter = new Intl.DateTimeFormat("en-IN", options);
      const dateString = formatter.format(utcDate);
      return dateString;
    }
    return "N/A";
  };

  return (
    <div>
      <ScannedLeadsNotes
        setTriggerNotes={setTriggerNotes}
        triggerNotes={triggerNotes}
        eventsId={eventsId.params.eventId}
        singleLeadsData={singleLeadsData}
        setAttendeesData={setAttendeesData}
        setSingleLeadsData={setSingleLeadsData}
        fetch={fetch}
        currentLead={currentLead}
      />

      <div className="w-full h-[60px] fixed justify-between top-0 bg-white flex items-center px-[16px] border-b border-[#EDEDED] z-20 md:mt-[59px] md:hidden">
        <div className="flex items-center">
          <img
            src="/svgs/Arrowleft.svg"
            className="w-[24px] h-[24px] object-cover cursor-pointer"
            alt="arrow"
            onClick={() => navigate("/events")}
          />
          <span className="ml-2 text-[22px] font-[500]">Leads</span>
        </div>

        <div
          className="bg-primary text-white px-2 py-1 text-[14px] ml-4 font-semibold rounded-[6px] inline-flex items-center text-center justify-center cursor-pointer hover:opacity-95 "
          onClick={() => {
            if (loading) return;
            handleDownload();
          }}
        >
          {loading ? (
            "Processing..."
          ) : (
            <div className="flex items-center text-center">
              Export
              <img
                src="/svgs/downloadIcon.svg"
                className="w-[17px] ml-2 font-[500]"
                alt=""
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-between items-center mt-[75px] md:mt-[30px] gap-4 px-4 md:w-[400px] w-full">
        <div className="border w-full rounded-[10px] py-4 px-5 flex justify-center items-center flex-col gap-1 shadow-[0_0px_8px_1px_rgba(0,0,0,0.1)] ">
          <p className="font-medium text-red-500 text-[19px] leading-none">
            {leadCount?.hot}
          </p>
          <p className="font-medium text-[13px] text-gray-500 leading-none mt-1.5">
            Hot
          </p>
        </div>
        <div className="border w-full rounded-[10px] py-4 px-5 flex justify-center items-center flex-col gap-1 shadow-[0_0px_8px_1px_rgba(0,0,0,0.1)] ">
          <p className="font-medium text-yellow-300 text-[19px] leading-none">
            {leadCount?.warm}
          </p>
          <p className="font-medium text-[13px] text-gray-500 leading-none mt-1.5">
            Warm
          </p>
        </div>
        <div className="border w-full rounded-[10px] py-4 px-5 flex justify-center items-center flex-col gap-1 shadow-[0_0px_8px_1px_rgba(0,0,0,0.1)] ">
          <p className="font-medium text-gray-500 text-[19px] leading-none">
            {leadCount?.cold}
          </p>
          <p className="font-medium text-[13px] text-gray-500 leading-none mt-1.5">
            Cold
          </p>
        </div>
      </div>

      <div>
        <form
          className="flex items-center md:w-[400px] w-full justify-center bg-[#FAFAFA] mt-[20px] px-4"
          onSubmit={(event) => event.preventDefault()}
        >
          <div className="relative w-full mt-0">
            <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
              <img src="/svgs/Search.svg" className="h-[19px]" alt="search" />
            </div>
            <input
              type="text"
              id="simple-search"
              className="bg-[#FAFAFA] border border-gray-300 text-gray-900 rounded-lg focus:ring-primary focus:border-primary block w-full pl-10 pr-10 pt-2 pb-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary dark:focus:border-primary text-[14px]"
              placeholder={`Search ${
                currentLead ? currentLead : "all"
              } leads...`}
              autoComplete="off"
              required={true}
              onChange={(event) => setSearchTerm(event.target.value?.trim())}
              // autoFocus
            />
          </div>
        </form>
      </div>

      <div className="md:flex items-center gap-2 mt-[15px]">
        <div className="md:w-[400px] w-full">
          <Listbox value={currentLead} onChange={setCurrentLead}>
            <div className="relative mx-4 ">
              <Listbox.Button className="relative cursor-pointer bg-white py-1 pl-2 pr-10 text-left focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 text-[14px] h-[41px] border  font-medium text-[#1C1C1E] w-full shadow rounded-[8px]">
                <div className="flex items-center gap-1.5 truncate capitalize ">
                  <svg
                    width="21px"
                    height="21px"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                    <g
                      id="SVGRepo_tracerCarrier"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></g>
                    <g id="SVGRepo_iconCarrier">
                      <g id="Interface / Filter">
                        <path
                          id="Vector"
                          d="M20 5.6001C20 5.04005 19.9996 4.75981 19.8906 4.5459C19.7948 4.35774 19.6423 4.20487 19.4542 4.10899C19.2403 4 18.9597 4 18.3996 4H5.59961C5.03956 4 4.75981 4 4.5459 4.10899C4.35774 4.20487 4.20487 4.35774 4.10899 4.5459C4 4.75981 4 5.04005 4 5.6001V6.33736C4 6.58195 4 6.70433 4.02763 6.81942C4.05213 6.92146 4.09263 7.01893 4.14746 7.1084C4.20928 7.20928 4.29591 7.29591 4.46875 7.46875L9.53149 12.5315C9.70443 12.7044 9.79044 12.7904 9.85228 12.8914C9.90711 12.9808 9.94816 13.0786 9.97266 13.1807C10 13.2946 10 13.4155 10 13.6552V18.411C10 19.2682 10 19.6971 10.1805 19.9552C10.3382 20.1806 10.5814 20.331 10.8535 20.3712C11.1651 20.4172 11.5487 20.2257 12.3154 19.8424L13.1154 19.4424C13.4365 19.2819 13.5966 19.2013 13.7139 19.0815C13.8176 18.9756 13.897 18.8485 13.9453 18.7084C14 18.5499 14 18.37 14 18.011V13.6626C14 13.418 14 13.2958 14.0276 13.1807C14.0521 13.0786 14.0926 12.9808 14.1475 12.8914C14.2089 12.7911 14.2947 12.7053 14.4653 12.5347L14.4688 12.5315L19.5315 7.46875C19.7044 7.2958 19.7904 7.20932 19.8523 7.1084C19.9071 7.01893 19.9482 6.92146 19.9727 6.81942C20 6.70551 20 6.58444 20 6.3448V5.6001Z"
                          stroke="#000000"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        ></path>
                      </g>
                    </g>
                  </svg>
                  <p className="pt-0.5">
                    {currentLead ? currentLead : "All"} Leads - (
                    {
                      leadCount[
                        currentLead ? currentLead?.toLowerCase() : "all"
                      ]
                    }
                    )
                  </p>
                </div>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <svg
                    width="21"
                    height="21"
                    viewBox="0 0 24 24"
                    fill="none"
                    className=" font-[600] cursor-pointer rotate-180"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.999 7.65454C11.8275 7.65454 11.6698 7.68774 11.5259 7.75415C11.382 7.82056 11.2437 7.92017 11.1108 8.05298L4.86035 14.4363C4.65007 14.6521 4.54492 14.915 4.54492 15.2249C4.54492 15.4241 4.59473 15.6095 4.69434 15.781C4.79395 15.9526 4.92952 16.0909 5.10107 16.196C5.26709 16.2957 5.45247 16.3455 5.65723 16.3455C5.96712 16.3455 6.23828 16.2292 6.4707 15.9968L11.999 10.3108L17.5273 15.9968C17.7653 16.2292 18.0365 16.3455 18.3408 16.3455C18.5511 16.3455 18.7393 16.2957 18.9053 16.196C19.0713 16.0909 19.2041 15.9526 19.3037 15.781C19.4033 15.6095 19.4531 15.4241 19.4531 15.2249C19.4531 14.915 19.348 14.6521 19.1377 14.4363L12.8872 8.05298C12.7544 7.91463 12.616 7.81502 12.4722 7.75415C12.3228 7.68774 12.165 7.65454 11.999 7.65454Z"
                      fill="#121212"
                      fillOpacity=""
                    />
                  </svg>
                </span>
              </Listbox.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute mt-1 font-medium max-h-[170px] w-[100%] overflow-auto rounded-[8px] bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none text-[14px] border z-20 mr-4">
                  {options.map((opt) => (
                    <Listbox.Option
                      key={opt.value}
                      className={({ active }) =>
                        `relative cursor-default select-none py-1 pl-3 pr-0 ${
                          active ? "bg-gray-100" : ""
                        }`
                      }
                      value={opt.value}
                    >
                      {({ selected }) => (
                        <>
                          <p
                            className={`cursor-pointer truncate gap-1.5 flex items-center ${
                              selected
                                ? "font-medium text-primary"
                                : "font-medium"
                            }`}
                          >
                            <p className="w-4">
                              {selected && (
                                <svg
                                  width="14px"
                                  height="14px"
                                  className=" fill-primary"
                                  version="1.1"
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 335.765 335.765"
                                >
                                  <g
                                    id="SVGRepo_bgCarrier"
                                    stroke-width="0"
                                  ></g>
                                  <g
                                    id="SVGRepo_tracerCarrier"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  ></g>
                                  <g id="SVGRepo_iconCarrier">
                                    <g>
                                      <g>
                                        <polygon points="311.757,41.803 107.573,245.96 23.986,162.364 0,186.393 107.573,293.962 335.765,65.795 "></polygon>
                                      </g>
                                    </g>
                                  </g>
                                </svg>
                              )}
                            </p>
                            {opt.label} - (
                            {leadCount[opt?.value ? opt?.value : "all"]})
                          </p>
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>
        </div>
        <div
          className="bg-primary text-white px-2 py-1 text-[14px] font-semibold rounded-[6px] mymd:inline-flex items-center text-center justify-center cursor-pointer hover:opacity-95 hidden"
          onClick={() => {
            if (loading) return;
            handleDownload();
          }}
        >
          {loading ? (
            "Processing..."
          ) : (
            <div className="flex items-center text-center">
              Export
              <img
                src="/svgs/downloadIcon.svg"
                className="w-[17px] ml-2 font-[500]"
                alt=""
              />
            </div>
          )}
        </div>
      </div>

      <div className="md:w-[100%] mymd:mt-[20px] mt-[20px] mb-[20px]">
        {isLoadingCounts || isLoadingLeads ? (
          <div className="mx-[16px] md:w-[800px] gap-5 md:flex flex-wrap">
            {[1, 2, 3, 4, 5].map((num, key) => {
              return (
                <div
                  className="bg-[#FFFFFF] mb-4 rounded-[8px] p-[16px] md:border md:w-[350px] mt-[10px] border border-[#EDEDED]"
                  key={num}
                >
                  <div className="bg-[#DDDBDD] w-full h-[45px] custom-pulse rounded-[8px]"></div>
                  <div className="bg-gray-200 w-full mt-2 h-[32px] custom-pulse rounded-[8px]"></div>
                  <div className="bg-[#DDDBDD] w-full mt-2 h-[65px] custom-pulse rounded-[8px]"></div>
                </div>
              );
            })}
          </div>
        ) : attendeesData?.length > 0 ? (
          <div className="md:flex flex flex-col md:flex-row flex-wrap gap-3">
            {attendeesData?.slice()?.map((attendee, key) => (
              <div
                className="mx-[16px]"
                key={key}
                onClick={() => {
                  setTriggerNotes(true);
                  setSingleLeadsData(attendee);
                }}
              >
                <div
                  className="bg-white mb-0 rounded-[10px] px-[14px] py-[15px] md:border md:w-[350px] mt-[0px] border border-[#EDEDED]"
                  key={attendee?.attendee._id}
                >
                  {/* <p className="mb-2 bg-gray-100 rounded-md px-1 py-0.5 text-sm inline-flex">
                      {attendee?.attendee?.email}
                    </p> */}
                  <div className="flex gap-2">
                    <div className=" min-w-[45px] max-w-[45px]">
                      {attendee.attendee?.profilePicture ? (
                        <img
                          src={attendee.attendee?.profilePicture}
                          className="rounded-full w-[43px] h-[43px] object-cover"
                          alt="ProfilePic"
                        />
                      ) : (
                        <div className="">
                          <DefaultProfilePicture
                            firstName={attendee.attendee.firstName}
                            lastName={attendee.attendee.lastName}
                            style={{
                              width: "43px",
                              height: "43px",
                              borderRadius: "100%",
                              fontSize: "16px",
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex justify-between w-full items-start gap-1.5">
                      <div>
                        <div className="flex flex-col justify-between w-full">
                          <div className="sm:text-[14px] text-[15x] font-[500] pt-[px] md:font-semibold line-clamp-1">
                            {attendee.attendee.firstName}{" "}
                            {attendee.attendee.lastName}{" "}
                          </div>
                          <div className="text-[14px] mt-0.5 font-normal text-gray-500 py-[0px] line-clamp-1">
                            {attendee.attendee.jobTitle}
                          </div>
                          <div className="text-[13px] mt-0.5 font-normal text-gray-500 py-[0px] line-clamp-1">
                            {attendee?.attendee?.organization}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p
                          className={`${
                            attendee?.qualifiedAs === "hot"
                              ? "bg-red-500"
                              : attendee?.qualifiedAs === "warm"
                              ? "bg-yellow-400"
                              : attendee?.qualifiedAs === "cold"
                              ? "bg-blue-500"
                              : "bg-gray-400"
                          } text-white inline-flex rounded-[10px] text-[11px] px-2.5 py-0.5 capitalize`}
                        >
                          {attendee?.qualifiedAs || "Unqualified"}
                        </p>
                        <div className="flex items-center gap-[3px] cursor-pointer">
                          {[...Array(5)].map((star, index) => {
                            return (
                              <div className="mt-2" key={index}>
                                {attendee?.leadRating >= index + 1 ? (
                                  <svg
                                    width={17}
                                    viewBox="0 0 32 32"
                                    version="1.1"
                                    fill="#d7bc0f"
                                    stroke="#d7bc0f"
                                    className="cursor-pointer"
                                  >
                                    <g
                                      id="SVGRepo_bgCarrier"
                                      stroke-width="0"
                                    ></g>
                                    <g
                                      id="SVGRepo_tracerCarrier"
                                      stroke-linecap="round"
                                      stroke-linejoin="round"
                                    ></g>
                                    <g id="SVGRepo_iconCarrier">
                                      <defs> </defs>
                                      <g
                                        id="Page-1"
                                        stroke="none"
                                        stroke-width="1"
                                        fill="none"
                                        fill-rule="evenodd"
                                      >
                                        <g
                                          id="Icon-Set-Filled"
                                          transform="translate(-154.000000, -881.000000)"
                                          fill="#e1c50e"
                                        >
                                          <path
                                            d="M186,893.244 L174.962,891.56 L170,881 L165.038,891.56 L154,893.244 L161.985,901.42 L160.095,913 L170,907.53 L179.905,913 L178.015,901.42 L186,893.244"
                                            id="start-favorite"
                                          ></path>
                                        </g>
                                      </g>
                                    </g>
                                  </svg>
                                ) : (
                                  <svg
                                    viewBox="0 0 32 32"
                                    version="1.1"
                                    fill="#000000"
                                    width={17}
                                    className="cursor-pointer"
                                  >
                                    <g
                                      id="SVGRepo_bgCarrier"
                                      stroke-width="0"
                                    ></g>
                                    <g
                                      id="SVGRepo_tracerCarrier"
                                      stroke-linecap="round"
                                      stroke-linejoin="round"
                                    ></g>
                                    <g id="SVGRepo_iconCarrier">
                                      <defs> </defs>{" "}
                                      <g
                                        id="Page-1"
                                        stroke="none"
                                        stroke-width="1"
                                        fill="none"
                                        fill-rule="evenodd"
                                      >
                                        <g
                                          id="Icon-Set"
                                          transform="translate(-152.000000, -879.000000)"
                                          fill="#7a7a7a"
                                        >
                                          <path
                                            d="M168,903.21 L160.571,907.375 L161.989,898.971 L155.594,892.442 L164.245,891.317 L168,883.313 L171.722,891.317 L180.344,892.54 L174.011,899.002 L175.335,907.406 L168,903.21 L168,903.21 Z M184,891.244 L172.962,889.56 L168,879 L163.038,889.56 L152,891.244 L159.985,899.42 L158.095,911 L168,905.53 L177.905,911 L176.015,899.42 L184,891.244 L184,891.244 Z"
                                            id="start-favorite"
                                          ></path>
                                        </g>
                                      </g>
                                    </g>
                                  </svg>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-right text-green-600 text-[11px] bg-green-100 inline-flex px-2 py-0.5 rounded-[6px] mt-1">
                      {formatDate(attendee?.meetingTime)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid w-full place-items-center h-[250px] md:w-[65%]">
            <div>
              <img
                src="/svgs/nullState.svg"
                alt=""
                className="w-[200px] h-[200px]"
              />
              <p className="text-[15px] font-[500] text-[#717171]  text-center">
                Nothing here...
              </p>
            </div>
          </div>
        )}
        <div ref={loadMoreRef}></div>
      </div>
    </div>
  );
};

export default ScannedLeads;
